import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { ContentCopy, Check } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import ReferenceMarker from './ReferenceMarker';
import MessageToolUse from './MessageToolUse';

const CodeBlock = React.memo(({ inline, className, children, ...props }) => {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  const codeString = String(children).replace(/\n$/, '');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  // Inline code
  if (inline) {
    return (
      <Box
        component="code"
        sx={{
          bgcolor: 'action.hover',
          color: 'primary.main',
          px: 0.75,
          py: 0.25,
          borderRadius: 0.5,
          fontSize: '0.9em',
          fontFamily: 'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
          border: '1px solid',
          borderColor: 'divider',
        }}
        {...props}
      >
        {children}
      </Box>
    );
  }

  // Code block
  return (
    <Box
      sx={{
        position: 'relative',
        my: 2,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      {/* Header with language and copy button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1,
          bgcolor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {language || 'code'}
        </Typography>
        <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              color: copied ? 'success.main' : 'text.secondary',
              '&:hover': {
                bgcolor: 'action.selected',
              },
            }}
          >
            {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Code content */}
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: '600px',
          bgcolor: 'rgba(0, 0, 0, 0.03)',
          '&::-webkit-scrollbar': {
            height: '8px',
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'action.disabled',
            borderRadius: '4px',
            '&:hover': {
              bgcolor: 'action.active',
            },
          },
        }}
      >
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            fontSize: '0.875rem',
            lineHeight: 1.6,
            fontFamily: 'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
            color: 'text.primary',
            whiteSpace: 'pre',
            wordWrap: 'normal',
            overflowWrap: 'normal',
          }}
        >
          <Box
            component="code"
            className={className}
            sx={{
              fontFamily: 'inherit',
              fontSize: 'inherit',
            }}
            {...props}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

const processInlineContent = (children, references, images) => {
  if (typeof children === 'string') {
    // References and images are now handled as separate segments in ChatMessage
    // This function now only handles basic text processing
    return children;
  }

  if (Array.isArray(children)) {
    return children.map((child, index) => {
      if (typeof child === 'string') {
        return processInlineContent(child, references, images);
      }
      return child;
    });
  }

  return children;
};

const preprocessLatex = (text) => {
  if (!text) return text;

  // Fix malformed markdown: convert multiplication symbols (∗) to asterisks (*) for proper bold/italic rendering
  // This handles cases where text was copied from sources that converted * to × or ∗
  let processedText = text.replace(/∗∗/g, '**').replace(/∗/g, '*');

  // Most modern LLMs already properly format LaTeX with $ and $$ delimiters
  // We only need minimal preprocessing to handle edge cases

  let inCodeBlock = false;
  let inMathBlock = false;
  const lines = processedText.split('\n');
  
  const processedLines = lines.map((line) => {
    // Track code blocks - don't process LaTeX inside code blocks
    if (line.trim().startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      return line;
    }

    if (inCodeBlock) {
      return line;
    }

    // Track display math blocks ($$...$$) - don't double-process
    if (line.trim().startsWith('$$')) {
      inMathBlock = !inMathBlock;
      return line;
    }

    if (inMathBlock) {
      return line;
    }

    // If line already contains properly formatted LaTeX ($ or $$), leave it as is
    if (line.includes('$$') || /\$[^$]+\$/.test(line)) {
      return line;
    }

    // Only wrap inline LaTeX that isn't already wrapped
    // Look for common LaTeX patterns that are clearly math but not wrapped
    let processedLine = line;

    // Handle naked subscripts/superscripts for variables (e.g., P_{i,j}, x^2, C_{i,j})
    // Only if they're clearly math notation and not already wrapped
    processedLine = processedLine.replace(
      /(?<!\$)([A-Z][a-zA-Z]*|[a-z])([_^]\{[^}]+\}(?:[_^]\{[^}]+\})?)(?!\$)/g,
      (match, variable, subscript) => {
        // Don't wrap if surrounded by word characters (likely part of prose)
        return `$${variable}${subscript}$`;
      }
    );

    return processedLine;
  });

  processedText = processedLines.join('\n');
  return processedText;
};

const MessageContent = React.memo(({ message, model, processedText, references = [], images = [], toolUse = [] }) => {
  // Memoize images array reference to prevent unnecessary re-renders
  const stableImages = useMemo(() => images, [images.length, images.map(img => img.filename).join(',')]);
  
  // Memoize the inline content processor function to maintain stable references
  const inlineProcessor = useMemo(() => {
    return (children) => processInlineContent(children, references, stableImages);
  }, [references, stableImages]);

  // Preprocess text to wrap LaTeX formulas and strip any remaining <ref> tags
  const preprocessedText = useMemo(() => {
    let text = preprocessLatex(processedText);
    return text;
  }, [processedText]);

  // Memoize the processed content to prevent unnecessary re-renders
  const processedChildren = useMemo(() => {
    if (!preprocessedText) return null;

    return (
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[
            [rehypeKatex, {
              strict: false,
              trust: true,
              throwOnError: false,
              displayMode: false,
              output: 'htmlAndMathml',
              fleqn: false,
              macros: {
                "\\geq": "\\ge",
                "\\leq": "\\le",
              }
            }],
            rehypeRaw
          ]}
          components={{
          // Code blocks and inline code
          code: CodeBlock,
          // Headings
          h1: ({ children }) => (
            <Typography variant="h4" component="h1" sx={{ mb: 2, mt: 2, fontWeight: 600 }}>
              {inlineProcessor(children)}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography variant="h5" component="h2" sx={{ mb: 1.5, mt: 1.5, fontWeight: 600 }}>
              {inlineProcessor(children)}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography variant="h6" component="h3" sx={{ mb: 1, mt: 1, fontWeight: 600 }}>
              {inlineProcessor(children)}
            </Typography>
          ),
          h4: ({ children }) => (
            <Typography variant="subtitle1" component="h4" sx={{ mb: 1, mt: 1, fontWeight: 600 }}>
              {inlineProcessor(children)}
            </Typography>
          ),
          h5: ({ children }) => (
            <Typography variant="subtitle2" component="h5" sx={{ mb: 0.5, mt: 0.5, fontWeight: 600 }}>
              {inlineProcessor(children)}
            </Typography>
          ),
          h6: ({ children }) => (
            <Typography variant="body2" component="h6" sx={{ mb: 0.5, mt: 0.5, fontWeight: 600 }}>
              {inlineProcessor(children)}
            </Typography>
          ),
            p: ({ children }) => (
              <Typography 
                component="div" 
                sx={{ mb: 1, '&:last-child': { mb: 0 } }}
              >
              {inlineProcessor(children)}
              </Typography>
            ),
          blockquote: ({ children }) => (
            <Box sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 1,
              my: 2,
              bgcolor: 'action.hover',
              borderRadius: 1
            }}>
              {inlineProcessor(children)}
            </Box>
          ),
          strong: ({ children }) => (
            <Box component="strong" sx={{ fontWeight: 600 }}>
              {inlineProcessor(children)}
            </Box>
          ),
          em: ({ children }) => (
            <Box component="em" sx={{ fontStyle: 'italic' }}>
              {inlineProcessor(children)}
            </Box>
          ),
            ul: ({ children }) => (
              <Box component="ul" sx={{ m: 0, pl: 0, listStylePosition: 'inside' }}>
                {children}
              </Box>
            ),
            ol: ({ children }) => (
              <Box component="ol" sx={{ m: 0, pl: 0, listStylePosition: 'inside' }}>
                {children}
              </Box>
            ),
            li: ({ children }) => {
              const processedChildren = React.Children.map(children, child => {
                if (React.isValidElement(child) && child.type === Typography && child.props.component === 'div') {
                  return React.cloneElement(child, {
                    sx: { ...child.props.sx, mb: 0, display: 'inline' }
                  });
                }
                return child;
              });
              
              return (
                <Typography 
                  component="li" 
                  sx={{ 
                    mb: 0.5,
                    display: 'list-item',
                    '&::marker': { color: 'text.primary' },
                    pl: 1,
                    textIndent: '-1em',
                    paddingInlineStart: '1em',
                    '& > div': {
                      display: 'inline',
                      mb: 0
                    }
                  }}
                >
                {processedChildren || inlineProcessor(children)}
                </Typography>
              );
            },
            table: ({ children }) => (
              <Box sx={{ width: '100%', overflowX: 'auto', my: 3, display: 'block' }}>
                <Box
                  component="table" 
                  sx={{ 
                    width: '100%',
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    minWidth: '500px',
                    tableLayout: 'fixed',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {children}
                </Box>
              </Box>
            ),
            thead: ({ children }) => (
              <Box component="thead" sx={{ bgcolor: 'primary.lighter' }}>
                {children}
              </Box>
            ),
            tbody: ({ children }) => <Box component="tbody">{children}</Box>,
            tr: ({ children, ...props }) => (
              <Box 
                component="tr" 
                sx={{ 
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { bgcolor: 'action.hover' },
                  transition: 'background-color 0.15s ease',
                }}
                {...props}
              >
                {children}
              </Box>
            ),
            th: ({ children }) => (
              <Box 
                component="th" 
                sx={{ 
                  py: 2,
                  px: 2.5,
                  textAlign: 'left',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: 'primary.dark',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderRight: 'none' },
                  position: 'relative',
                }}
              >
                {children}
              </Box>
            ),
            td: ({ children, node }) => {
              const isJustHyphen = node.children?.length === 1 && 
                                node.children[0].type === 'text' && 
                                node.children[0].value === '-';
                                
              return (
                <Box 
                  component="td" 
                  sx={{ 
                    py: 1.5,
                    px: 2.5,
                    textAlign: 'left',
                    fontSize: '0.875rem',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderRight: 'none' },
                    wordBreak: 'break-word',
                  }}
                >
                  {isJustHyphen ? '-' : (children || "-")}
                </Box>
              );
            },
            img: ({ src, alt }) => {
              return (
                <Box sx={{ my: 2 }}>
                  <img
                    src={src}
                    alt={alt}
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      display: 'block'
                    }}
                  />
                  {alt && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 1,
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        textAlign: 'center'
                      }}
                    >
                      {alt}
                    </Typography>
                  )}
                </Box>
              );
            },
            // Handle any remaining <ref> tags that might slip through processing
            ref: () => null,
          }}
        >
        {preprocessedText}
        </ReactMarkdown>
    );
  }, [preprocessedText, inlineProcessor]);

  if (message.sender === 'user') {
    return (
      <Box>
        <Typography sx={{ whiteSpace: 'pre-wrap' }}>{message.text}</Typography>
        {message.hasAudio && message.audioFile && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <audio
              controls
              style={{ maxWidth: '200px' }}
              src={message.audioFile}
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <>
      {model.includes('DeepSeek') && message.thinkingContent && (
        <Box sx={{
          position: 'relative',
          mb: 3,
          p: 2,
          pl: 3,
          borderRadius: 1,
          bgcolor: 'rgba(0, 0, 0, 0.04)',
          borderLeft: '3px solid',
          borderColor: 'primary.light',
          '&::before': {
            content: '"Thinking..."',
            position: 'absolute',
            top: -10,
            left: 12,
            fontSize: '0.7rem',
            fontWeight: 600,
            color: 'primary.light',
            bgcolor: 'background.paper',
            px: 1,
            borderRadius: 1,
          }
        }}>
          <Typography
            variant="body2"
            sx={{
              fontStyle: 'italic',
              color: 'text.secondary',
              fontSize: '0.9rem',
              lineHeight: 1.5,
            }}
          >
            {message.thinkingContent || (message.text.includes('</think>') ? message.text.split('</think>')[0] : '')}
          </Typography>
        </Box>
      )}

      {toolUse && toolUse.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <MessageToolUse toolUse={toolUse} message={message} />
        </Box>
      )}

      {(!model.includes('DeepSeek') || message.responseContent ||
        (message.text.includes('</think>') && !message.isThinking)) && processedChildren}
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  // Return true if props are equal (should NOT re-render)
  // Return false if props are different (should re-render)
  
  // Always re-render if processedText changes
  if (prevProps.processedText !== nextProps.processedText) {
    return false;
  }
  
  // Check if images have actually changed (by comparing filenames)
  const prevImageFilenames = prevProps.images.map(img => img.filename).join(',');
  const nextImageFilenames = nextProps.images.map(img => img.filename).join(',');
  if (prevImageFilenames !== nextImageFilenames) {
    return false;
  }
  
  // Check if references have changed
  const prevRefs = prevProps.references.join(',');
  const nextRefs = nextProps.references.join(',');
  if (prevRefs !== nextRefs) {
    return false;
  }
  
  // Check if toolUse has changed
  const prevToolUse = JSON.stringify(prevProps.toolUse);
  const nextToolUse = JSON.stringify(nextProps.toolUse);
  if (prevToolUse !== nextToolUse) {
    return false;
  }
  
  // Check relevant message properties
  if (prevProps.message.isThinking !== nextProps.message.isThinking) {
    return false;
  }
  
  if (prevProps.message.thinkingContent !== nextProps.message.thinkingContent) {
    return false;
  }
  
  if (prevProps.message.responseContent !== nextProps.message.responseContent) {
    return false;
  }
  
  // If model changes, re-render
  if (prevProps.model !== nextProps.model) {
    return false;
  }
  
  // Props are equal, don't re-render
  return true;
});

export default MessageContent;

